import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import type { User } from '../types';
import type { RegistrationRequestResponse } from '@ehes/contracts';

type AuthValue = {
  user: User | null; loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (fullName: string, companyName: string, address: string, taxNumber: string | null, email: string, password: string) => Promise<RegistrationRequestResult>;
  registerDriver: (fullName: string, phone: string | null, licenseNumber: string | null, email: string, password: string) => Promise<RegistrationRequestResult>;
  verifyEmail: (token: string) => Promise<{ message: string }>;
  logout: () => void; refresh: () => Promise<void>;
};
type RegistrationRequestResult = RegistrationRequestResponse;
const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    if (!localStorage.getItem('ehes_token')) { setLoading(false); return; }
    try { const data = await api<{ user: User }>('/auth/me'); setUser(data.user); }
    catch { localStorage.removeItem('ehes_token'); setUser(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { void refresh(); }, []);
  const accept = (data: { token: string; user: User }) => { localStorage.setItem('ehes_token', data.token); setUser(data.user); return data.user; };
  const value = useMemo<AuthValue>(() => ({
    user, loading,
    login: async (email, password) => accept(await api('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) })),
    register: async (fullName, companyName, address, taxNumber, email, password) => api('/auth/register/company', { method: 'POST', body: JSON.stringify({ fullName, companyName, address, taxNumber, email, password }) }),
    registerDriver: async (fullName, phone, licenseNumber, email, password) => api('/auth/register/driver', { method: 'POST', body: JSON.stringify({ fullName, phone, licenseNumber, email, password }) }),
    verifyEmail: async token => api('/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),
    logout: () => { localStorage.removeItem('ehes_token'); setUser(null); }, refresh,
  }), [user, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export const useAuth = () => { const value = useContext(AuthContext); if (!value) throw new Error('AuthProvider missing'); return value; };
